// src/pages/TransactionDetailPage.tsx
// User-facing transaction detail page
// Canon Ref: CANON.md v1.3.0 (Design Canon, Frontend Data Canon)
// PRD: PRD-04-transactions-history.md

import { TransactionStatusBadge } from '@/components/TransactionStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useTransactionDetail } from '@/hooks/useTransactionDetail';
import { useTransactionTimeline } from '@/hooks/useTransactionTimeline';
import { mapKindToDirection, mapKindToDisplayType } from '@/lib/transactions/mapping';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { EmptyState, Page, PageContent, PageHeader, PageToolbar } from '@supabank/ui';
import { format } from 'date-fns';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';

interface TransactionDetailPageProps {
  id: string;
  onBack?: () => void;
}

export const TransactionDetailPage: React.FC<TransactionDetailPageProps> = ({ id, onBack }) => {
  const navigate = useNavigate();
  const { data: detailData, isLoading: detailLoading, error: detailError } = useTransactionDetail(id);
  const { data: timelineData, isLoading: timelineLoading, error: timelineError } = useTransactionTimeline(id);

  // Validate data structure to prevent App Unavailable errors
  const transaction = detailData?.transaction;
  const isValidData = detailData && transaction && typeof transaction === 'object';

  const displayData = useMemo(() => {
    if (!transaction || !isValidData) return null;

    try {
      // Improved amount logic based on transaction kind
      let displayAmount = 0;
      let displayCurrency = 'USD';

      if (transaction.kind === 'drain' || transaction.kind === 'va_deposit') {
        // Incoming: use amount_in
        displayAmount = transaction.amount_in ?? 0;
        displayCurrency = transaction.asset_in || 'USD';
      } else if (transaction.kind === 'payout') {
        // Outgoing: use amount_out
        displayAmount = transaction.amount_out ?? 0;
        displayCurrency = transaction.asset_out || 'USD';
      } else {
        // Transfer/return: prefer amount_out, fallback to amount_in
        displayAmount = transaction.amount_out ?? transaction.amount_in ?? 0;
        displayCurrency = transaction.asset_out || transaction.asset_in || 'USD';
      }

      return {
        display_type: mapKindToDisplayType(transaction.kind || 'transfer'),
        display_amount: displayAmount,
        display_currency: displayCurrency,
        direction: mapKindToDirection(transaction.kind || 'transfer'),
        occurred_at: transaction.settled_at || transaction.created_at || new Date().toISOString(),
      };
    } catch (error) {
      console.error('[TransactionDetailPage] Error creating displayData:', error);
      return null;
    }
  }, [transaction, isValidData]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyAllDetails = () => {
    if (!transaction || !displayData) return;

    const details = [
      `Transaction Details - ${displayData.display_type}`,
      '',
      `Transaction ID: ${transaction.id}`,
      `Type: ${displayData.display_type}`,
      `Status: ${transaction.status}`,
      `Amount: ${displayData.display_amount} ${displayData.display_currency}`,
      transaction.amount_in && `Amount In: ${transaction.amount_in} ${transaction.asset_in}`,
      transaction.amount_out && `Amount Out: ${transaction.amount_out} ${transaction.asset_out}`,
      transaction.fee && `Fee: ${transaction.fee}`,
      `Created: ${format(new Date(transaction.created_at), 'PPpp')}`,
      transaction.settled_at && `Settled: ${format(new Date(transaction.settled_at), 'PPpp')}`,
      transaction.bridge_ids && Object.keys(transaction.bridge_ids).length > 0 && `Bridge IDs: ${JSON.stringify(transaction.bridge_ids, null, 2)}`,
    ].filter(Boolean).join('\n');

    copyToClipboard(details);
    toast.success('All details copied to clipboard');
  };

  const downloadDetails = () => {
    if (!transaction || !displayData) return;

    const details = [
      `Transaction Details - ${displayData.display_type}`,
      `Transaction ID: ${transaction.id}`,
      `Type: ${displayData.display_type}`,
      `Status: ${transaction.status}`,
      `Kind: ${transaction.kind}`,
      `Direction: ${displayData.direction}`,
      '',
      'Amounts:',
      `Display Amount: ${displayData.display_amount} ${displayData.display_currency}`,
      transaction.amount_in && `Amount In: ${transaction.amount_in} ${transaction.asset_in}`,
      transaction.amount_out && `Amount Out: ${transaction.amount_out} ${transaction.asset_out}`,
      transaction.fee && `Fee: ${transaction.fee}`,
      '',
      'Timestamps:',
      `Created: ${format(new Date(transaction.created_at), 'PPpp')}`,
      transaction.settled_at && `Settled: ${format(new Date(transaction.settled_at), 'PPpp')}`,
      transaction.updated_at && `Updated: ${format(new Date(transaction.updated_at), 'PPpp')}`,
      '',
      'Bridge IDs:',
      transaction.bridge_ids && Object.entries(transaction.bridge_ids).map(([key, value]) =>
        `${key}: ${value}`
      ).join('\n'),
      '',
      'Related Entities:',
      transaction.liquidation_address_id && `Liquidation Address ID: ${transaction.liquidation_address_id}`,
      transaction.virtual_account_id && `Virtual Account ID: ${transaction.virtual_account_id}`,
      transaction.external_account_id && `External Account ID: ${transaction.external_account_id}`,
      transaction.payout_route_id && `Payout Route ID: ${transaction.payout_route_id}`,
    ].filter(Boolean).join('\n');

    const blob = new Blob([details], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transaction-${transaction.id.slice(0, 8)}-${format(new Date(), 'yyyy-MM-dd')}.txt`;
    link.click();
    toast.success('Details downloaded');
  };

  const handleRefresh = () => {
    // Refetch transaction detail
    window.location.reload();
  };

  const toolbarItems: Array<{ id: 'refresh'; hotkey: 'R'; ariaLabel: string } | { id: 'export'; hotkey: 'E'; ariaLabel: string }> = [
    {
      id: 'refresh',
      hotkey: 'R',
      ariaLabel: 'Refresh transaction',
    },
    {
      id: 'export',
      hotkey: 'E',
      ariaLabel: 'Export transaction details',
    }
  ];

  const handleToolbarAction = (actionId: string) => {
    if (actionId === 'refresh') handleRefresh();
    if (actionId === 'export') downloadDetails();
  };

  // Loading state (CANON v1.3.0: Skeleton-first)
  if (detailLoading) {
    return (
      <Page data-ux-contract="canon">
        <PageHeader
          icon={() => <SFIcon icon={sfIcons.activity} />}
          title="Transaction Details"
          subtitle="Loading..."
        />
        <PageContent>
          <Skeleton className={cn('h-[300px] w-full', semanticTokens.radius.lg)} />
        </PageContent>
      </Page>
    );
  }

  // Error state (CANON v1.3.0: Error state with retry)
  // Check for errors or invalid data structure
  if (detailError || !isValidData || !transaction) {
    return (
      <Page data-ux-contract="canon">
        {onBack && (
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className={cn(
                "focus-visible:ring-2 focus-visible:ring-blue-500",
                semanticTokens.motion.standard
              )}
              aria-label="Back to transactions"
            >
              <SFIcon icon={sfIcons.back} className="w-4 h-4 mr-2" aria-hidden="true" />
              Back to Transactions
            </Button>
          </div>
        )}
        <PageHeader
          icon={() => <SFIcon icon={sfIcons.activity} />}
          title="Transaction Details"
          subtitle="Error loading transaction"
        />
        <PageContent>
          <EmptyState
            icon={() => <SFIcon icon={sfIcons.warning} />}
            title="Transaction not found"
            description={detailError?.message || "The requested transaction could not be found or you don't have access to it."}
            action={{
              label: "Back to Transactions",
              onClick: onBack || (() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/dashboard/activity');
                }
              })
            }}
          />
        </PageContent>
      </Page>
    );
  }

  // Additional safety check - if displayData is null, show error
  if (!displayData) {
    return (
      <Page data-ux-contract="canon">
        {onBack && (
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className={cn("focus-visible:ring-2 focus-visible:ring-blue-500", semanticTokens.motion.standard)}
              aria-label="Back to transactions"
            >
              <SFIcon icon={sfIcons.back} className="w-4 h-4 mr-2" aria-hidden="true" />
              Back to Transactions
            </Button>
          </div>
        )}
        <PageHeader
          icon={() => <SFIcon icon={sfIcons.activity} />}
          title="Transaction Details"
          subtitle="Error loading transaction data"
        />
        <PageContent>
          <EmptyState
            icon={() => <SFIcon icon={sfIcons.warning} />}
            title="Invalid transaction data"
            description="The transaction data could not be processed. Please try again or contact support."
            action={{
              label: "Back to Transactions",
              onClick: onBack || (() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/dashboard/activity');
                }
              })
            }}
          />
        </PageContent>
      </Page>
    );
  }

  return (
    <Page data-ux-contract="canon">
      {/* Back Button - Desktop only (mobile uses Page.Header back button) */}
      <div className="mb-4 hidden lg:block">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack || (() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/dashboard/activity');
            }
          })}
          className={cn("focus-visible:ring-2 focus-visible:ring-blue-500", semanticTokens.motion.standard)}
          aria-label="Back to transactions"
        >
          <SFIcon icon={sfIcons.back} className="w-4 h-4 mr-2" aria-hidden="true" />
          Back to Transactions
        </Button>
      </div>

      <PageHeader
        icon={() => <SFIcon icon={sfIcons.activity} />}
        title={displayData.display_type || 'Transaction'}
        subtitle={`Transaction ID: ${transaction.id?.slice(0, 8) || 'Unknown'}...`}
        showBackButton={true}
        onBack={onBack || (() => navigate('/dashboard/activity'))}
      />

      <PageToolbar items={toolbarItems} onAction={handleToolbarAction} />

      <PageContent>
        <div className="space-y-6">
          {/* Transaction Overview */}
          <Card className={semanticTokens.motion.standard}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={cn(semanticTokens.typography.h3)}>Transaction Overview</CardTitle>
                  <CardDescription className={cn(semanticTokens.typography.body, "mt-1")}>
                    {displayData?.display_type} • {format(new Date(displayData?.occurred_at || transaction.created_at), 'PPpp')}
                  </CardDescription>
                </div>
                <TransactionStatusBadge status={transaction.status} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Amount Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-2")}>
                    {transaction.amount_in ? 'Amount In' : 'Amount'}
                  </p>
                  <p className={cn(semanticTokens.typography.h2)}>
                    {displayData?.display_amount.toLocaleString()} {displayData?.display_currency}
                  </p>
                  {transaction.amount_in && transaction.asset_in && (
                    <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted, "mt-1")}>
                      {transaction.amount_in.toFixed(6)} {transaction.asset_in}
                    </p>
                  )}
                </div>
                <div>
                  <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-2")}>
                    {transaction.amount_out ? 'Amount Out' : 'Net Amount'}
                  </p>
                  <p className={cn(semanticTokens.typography.h2, semanticTokens.positive.fg)}>
                    {transaction.amount_out
                      ? `${(transaction.amount_out - (transaction.fee || 0)).toLocaleString()} ${transaction.asset_out}`
                      : `${displayData?.display_amount.toLocaleString()} ${displayData?.display_currency}`
                    }
                  </p>
                  {transaction.fee && transaction.fee > 0 && (
                    <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted, "mt-1")}>
                      Fee: {transaction.fee.toLocaleString()} {transaction.asset_out}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Transaction Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Type</p>
                  <Badge variant="secondary">{displayData?.display_type}</Badge>
                </div>
                <div>
                  <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Direction</p>
                  <Badge variant="outline" className="capitalize">{displayData?.direction}</Badge>
                </div>
                <div>
                  <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Kind</p>
                  <p className={cn(semanticTokens.typography.body, "font-mono text-sm")}>{transaction.kind}</p>
                </div>
                <div>
                  <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Status</p>
                  <TransactionStatusBadge status={transaction.status} />
                </div>
                <div>
                  <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Created</p>
                  <p className={cn(semanticTokens.typography.body, "text-sm")}>
                    {format(new Date(transaction.created_at), 'PPpp')}
                  </p>
                </div>
                {transaction.settled_at && (
                  <div>
                    <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Settled</p>
                    <p className={cn(semanticTokens.typography.body, "text-sm")}>
                      {format(new Date(transaction.settled_at), 'PPpp')}
                    </p>
                  </div>
                )}
              </div>

              {/* Bridge IDs */}
              {transaction.bridge_ids && Object.keys(transaction.bridge_ids).length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-2")}>Bridge IDs</p>
                    <div className="space-y-2">
                      {Object.entries(transaction.bridge_ids).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                            {key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </span>
                          <div className="flex items-center gap-2">
                            <code className={cn("text-xs font-mono", semanticTokens.fg.default)}>
                              {String(value)}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(String(value))}
                              className="h-6 w-6 p-0"
                              aria-label={`Copy ${key}`}
                            >
                              <SFIcon icon={sfIcons.copy} className="w-3 h-3" aria-hidden="true" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          {timelineData && !timelineError && timelineData.events && timelineData.events.length > 0 && (
            <Card className={semanticTokens.motion.standard}>
              <CardHeader>
                <CardTitle className={cn(semanticTokens.typography.h3)}>Timeline</CardTitle>
                <CardDescription className={cn(semanticTokens.typography.body)}>
                  Transaction events and status changes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {timelineData.events.map((event, index) => {
                    const getEventIcon = () => {
                      switch (event.event_type) {
                        case 'transaction.created':
                          return <SFIcon icon={sfIcons.pending} className="w-4 h-4" />;
                        case 'transaction.settled':
                          return <SFIcon icon={sfIcons.success} className={cn('w-4 h-4', semanticTokens.positive.fg)} />;
                        case 'transaction.status_changed':
                          return <SFIcon icon={sfIcons.warning} className={cn('w-4 h-4', semanticTokens.warning.fg)} />;
                        default:
                          return <SFIcon icon={sfIcons.pending} className="w-4 h-4" />;
                      }
                    };

                    return (
                      <div key={event.id || index} className="flex items-start gap-4">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center",
                          semanticTokens.bg.muted,
                          semanticTokens.radius.full,
                          "mt-1"
                        )}>
                          {getEventIcon()}
                        </div>
                        <div className="flex-1">
                          <p className={cn(semanticTokens.typography.label)}>{event.description}</p>
                          <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted, "mt-1")}>
                            {format(new Date(event.timestamp), 'PPpp')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Related Entities */}
          {(detailData?.liquidation_address || detailData?.virtual_account || detailData?.external_account || detailData?.payout_route) && (
            <Card className={semanticTokens.motion.standard}>
              <CardHeader>
                <CardTitle className={cn(semanticTokens.typography.h3)}>Related Entities</CardTitle>
                <CardDescription className={cn(semanticTokens.typography.body)}>
                  Entities associated with this transaction
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {detailData.liquidation_address && (
                    <div>
                      <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Liquidation Address</p>
                      <div className="flex items-center gap-2">
                        <code className={cn("text-xs font-mono", semanticTokens.fg.default)}>
                          {detailData.liquidation_address.address.slice(0, 8)}...
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/dashboard/receive`)} // Addresses are now on Receive page
                          className="h-6 px-2 text-xs"
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  )}
                  {detailData.virtual_account && (
                    <div>
                      <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Virtual Account</p>
                      <div className="flex items-center gap-2">
                        <code className={cn("text-xs font-mono", semanticTokens.fg.default)}>
                          {detailData.virtual_account.bridge_va_id?.slice(0, 8)}...
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/dashboard/virtual-accounts/${detailData.virtual_account!.id}`)}
                          className="h-6 px-2 text-xs"
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  )}
                  {detailData.external_account && (
                    <div>
                      <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>External Account</p>
                      <p className={cn(semanticTokens.typography.body, "text-sm")}>
                        {detailData.external_account.bank_name} ••••{detailData.external_account.last_4}
                      </p>
                    </div>
                  )}
                  {detailData.payout_route && (
                    <div>
                      <p className={cn(semanticTokens.typography.label, semanticTokens.fg.muted, "mb-1")}>Payout Route</p>
                      <p className={cn(semanticTokens.typography.body, "text-sm")}>
                        {detailData.payout_route.rail.toUpperCase()} • {detailData.payout_route.currency}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </PageContent>
    </Page>
  );
};

